import moment from "moment/min/moment-with-locales";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosInstance } from "../auth/Auth";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { useFetchData } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, InputSelection, ModulePage, SearchWithDownloadRow, SoleDescriptionRow } from "../components/pageSections";
import PredictorHeader from "../components/predictor/PredictorHeader";
import PredictorRow from "../components/predictor/PredictorRow";
import PredictorSetupBanner from "../components/predictor/PredictorSetupBanner";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";

export default function Predictor() {
    const moduleName = "predictor";

    const [formData, setFormData, saveFormData, loadFormData, hydrated] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData, setData] = useFetchData();
    const [setupLoading, setSetupLoading] = useState(false);
    const [setupErrorCode, setSetupErrorCode] = useState(null);
    const [missingSetup, setMissingSetup] = useState(null);

    const [filterData, setFilterData] = useState({ searchString: "" });
    const [showAll, setShowAll] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const presetStation = location.state?.presetStation;
    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();

    useEffect(() => {
        if (!hydrated || !presetStation) return;
        setFormData(current => (current.station[0]?.radio_id === presetStation.radio_id ? current : { ...current, station: [presetStation] }));
    }, [hydrated, presetStation, setFormData]);

    async function checkPredictorSetup(radioId) {
        try {
            await axiosInstance.get(`/predictor/settings/${radioId}`);
        } catch (err) {
            if (err.response?.status === 404) return { complete: false, openSetupForm: true };
            throw err;
        }

        try {
            const { data } = await axiosInstance.get(`/predictor/settings/${radioId}/weights`);
            return { complete: Array.isArray(data) && data.length > 0, openSetupForm: false };
        } catch (err) {
            if (err.response?.status === 404) return { complete: false, openSetupForm: false };
            throw err;
        }
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const radioId = formData.station[0].radio_id;
        setSetupLoading(true);
        setSetupErrorCode(null);
        try {
            const setup = await checkPredictorSetup(radioId);
            if (!setup.complete) {
                setData(null);
                setMissingSetup({ radioId, openSetupForm: setup.openSetupForm });
                return;
            }
        } catch (err) {
            console.error("Failed to check predictor setup", err);
            setData(null);
            setMissingSetup(null);
            setSetupErrorCode(err.response?.status ?? 500);
            return;
        } finally {
            setSetupLoading(false);
        }

        setMissingSetup(null);
        const url = `/predictor/${radioId}`;
        await fetchData(url);
    }

    function handleSetupClick() {
        navigate("/settings/predictor", { state: { predictorSetup: missingSetup } });
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.data;
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.song.artist.toLowerCase();
                    const title = x.song.title.toLowerCase();

                    return andParts.every(part => {
                        const orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                return artist === exact || title === exact;
                            }
                            return artist.includes(term) || title.includes(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);

    const displayedSongs = useMemo(() => {
        if (!songsMemo) return songsMemo;
        return showAll ? songsMemo : songsMemo.filter(x => x.stars >= 2);
    }, [songsMemo, showAll]);

    const hiddenCount = useMemo(() => (songsMemo ? songsMemo.filter(x => x.stars < 2).length : 0), [songsMemo]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(displayedSongs, { key: "stars", direction: "descending" });

    const csvHeaders = ["Artist", "Title", "Song Birth", "Market Spins", "Stars"];
    const csvData = useMemo(
        () => sortedItems?.map(x => [x.song.artist, x.song.title, x.song_birth ? moment(x.song_birth).format("L HH:mm") : "-", x.market_spins ?? "-", x.stars]),
        [sortedItems],
    );

    return (
        <ModulePage>
            <ModulePage.Title>Predictor</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 16}
                        loadOnChange={loadFormData}
                    />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading || setupLoading} errorCode={setupErrorCode || errorCode}>
                <DataContent.Placeholder show={!data && !missingSetup}>
                    <PageDesciption
                        title={"Predictor"}
                        whatList={[
                            "Easy analysis and suggestions for new music for your station",
                            "Based on a portfolio of stations or markets you look to for inspiriation – any station in your country or elsewhere can be added",
                            "The “star” rating shows how successful the song is on other stations in your predictor market",
                        ]}
                        knowList={[
                            {
                                text: "Predictor compares what you have played and are playing to other hand picked stations, and looks for “new hits” on those stations you are missing out on",
                            },
                            { text: "Together with the “Hybrid Hitlist” function this covers almost all or your new music needs for most formats" },
                            { text: "Predictor runs daily" },
                        ]}
                        videoSrc="https://www.youtube.com/embed/YogIgjBZRLM?si=XQSUp75gQWO0vR00"
                    />
                </DataContent.Placeholder>
                {missingSetup && <PredictorSetupBanner onButtonClick={handleSetupClick} />}
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                <DataDescription.Station radioId={data.radio_id} />
                                <DataDescription.DateRange startDate={data.basis_week} />
                                <DataDescription.Simple label="Generated date" value={moment(data?.generated_at).format("L LT")} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            csvHeaders={csvHeaders}
                            data={csvData}
                            filename={"Predictor"}
                        />
                        <PredictorHeader requestSort={requestSort} sortConfig={sortConfig} />
                        <Container fluid>
                            {sortedItems.map(item => (
                                <PredictorRow item={item} key={item.song.item_id} setCatalogueSong={setCatalogueSong} />
                            ))}
                            {!sortedItems?.length && <EmptyRow text="No songs found" />}
                        </Container>
                        {hiddenCount > 0 && (
                            <div className="text-center mt-2">
                                <Button variant="ra-dark-blue-outline" size="sm" onClick={() => setShowAll(v => !v)}>
                                    {showAll ? "Show top rated only" : `Show all songs (${hiddenCount} more)`}
                                </Button>
                            </div>
                        )}
                    </>
                )}
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
